import React, { useEffect, useState } from 'react';
import ticketService from '../../services/ticketService';
import RecordCreationFields from './RecordCreationFields';
import ProviderChangeConfigFields, { buildDefaultProviderChangeConfig, mergeProviderChangeConfig } from './ProviderChangeConfigFields';
import TicketProviderSelect from './TicketProviderSelect';
import { isCollaborativeTicket, isPaymentGatewayTicket, getTicketProviderDefinition } from '../../utils/ticketRequests';

const TicketRequestEditor = ({ ticket, onUpdate, onEditingChange, children }) => {
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loadingDraft, setLoadingDraft] = useState(false);
  const [optionsReady, setOptionsReady] = useState(false);
  const [recordOptions, setRecordOptions] = useState([]);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [targetProvider, setTargetProvider] = useState(null);
  const isEditing = !!draft;
  const isL1 = ticket.ticket_type === 'l1_create';
  const canEdit = !!ticket.workflow?.can_edit_request;
  const isAddProvider = ticket.ticket_type === 'add_provider';
  const paymentGateway = isPaymentGatewayTicket(ticket);
  const provider = getTicketProviderDefinition(isL1 ? draft?.l1_request?.data : targetProvider, paymentGateway, recordOptions);
  const updateDraft = (patch) => setDraft(previous => ({ ...previous, ...patch }));

  useEffect(() => {
    onEditingChange?.(isEditing);
  }, [isEditing, onEditingChange]);

  const startEditing = async () => {
    setLoadingDraft(true);
    setError('');
    setNotice('');
    const result = await ticketService.getTicketById(ticket._id);
    setLoadingDraft(false);
    if (!result.success) { setError(result.message || 'Unable to load the latest request.'); return; }
    const latest = result.data;
    onUpdate(latest);
    if (!latest.workflow?.can_edit_request) { setError('This request is no longer editable.'); return; }
    const request = isL1 ? latest.l1_request : null;
    setOptionsReady(!isL1);
    setTargetProvider(latest.provider_change?.to_master_provider_id || latest.provider_change?.from_master_provider_id || null);
    setDraft(JSON.parse(JSON.stringify({
      expected_revision: latest.request_revision || 0,
      ...(isL1 ? { l1_request: {
        module: request.module, data: request.data,
        ...(request.merchant_provider_config ? { merchant_provider_config: request.merchant_provider_config } : {})
      } } : { ...(isAddProvider ? { to_master_provider_id: latest.provider_change.to_master_provider_id?._id || '' } : {}), provider_config: latest.provider_change.provider_config })
    })));
  };

  const save = async event => {
    event.preventDefault();
    if (saving || (isL1 && !optionsReady)) return;
    setSaving(true);
    setError('');
    const payload = {
      expected_revision: draft.expected_revision,
      ...(isL1 ? { l1_request: { ...draft.l1_request, ...(draft.l1_request.merchant_provider_config ? {
        merchant_provider_config: mergeProviderChangeConfig(provider, draft.l1_request.merchant_provider_config)
      } : {}) } } : {
        provider_config: mergeProviderChangeConfig(provider, draft.provider_config),
        ...(isAddProvider ? { to_master_provider_id: draft.to_master_provider_id } : {})
      })
    };
    const result = await ticketService.updateTicketRequest(ticket._id, payload);
    setSaving(false);
    if (!result.success) { setError(result.message || 'Unable to save the request.'); return; }
    onUpdate(result.data);
    setDraft(null);
    setNotice('Changes saved.');
  };

  const showValidationError = event => {
    const field = event.target;
    let section = field.closest('details');
    while (section) {
      section.open = true;
      section = section.parentElement.closest('details');
    }
    setError(field.validationMessage);
  };

  const editButton = canEdit && !draft && <button type="button" className="btn btn-secondary" onClick={startEditing} disabled={loadingDraft}>
    {loadingDraft ? 'Loading...' : 'Edit configuration'}
  </button>;
  const providerFields = draft && <ProviderChangeConfigFields allowCustomFields={!paymentGateway} allowStatusChange={!isL1} provider={provider}
    value={isL1 ? draft.l1_request.merchant_provider_config : draft.provider_config}
    onChange={value => updateDraft(isL1 ? { l1_request: { ...draft.l1_request, merchant_provider_config: value } } : { provider_config: value })} disabled={saving} />;
  const form = draft && <form onSubmit={save} onInvalidCapture={showValidationError} className="ticket-request-form">
    {ticket.status === 'pending_review' && <p className="ticket-edit-intro">Saving configuration changes returns this request to In progress for review.</p>}
    {isAddProvider && <TicketProviderSelect ticketId={ticket._id} provider={targetProvider} disabled={saving} onChange={selected => {
      if (!selected || selected._id === targetProvider?._id) return;
      setTargetProvider(selected);
      updateDraft({ to_master_provider_id: selected._id, provider_config: {
        ...draft.provider_config,
        ...(paymentGateway ? { selling_rate_rules: buildDefaultProviderChangeConfig(getTicketProviderDefinition(selected, true)).selling_rate_rules } : {}),
        credentials: buildDefaultProviderChangeConfig(getTicketProviderDefinition(selected, paymentGateway)).credentials
      } });
    }} />}
    {isL1 && <RecordCreationFields level="l1" lockModule grouped ticketId={ticket._id} value={draft.l1_request} onOptions={setRecordOptions}
      onChange={value => updateDraft({ l1_request: value })} onReady={setOptionsReady} disabled={saving} />}
    {!isL1 && providerFields}
    {isL1 && draft.l1_request.merchant_provider_config && <details className="ticket-details-group">
      <summary>Merchant configuration</summary>{providerFields}
    </details>}
    {error && <div role="alert" className="ticket-inline-notice is-error">{error}</div>}
    <div className="ticket-approval-actions ticket-edit-footer">
      <button type="button" className="btn btn-secondary" disabled={saving} onClick={() => { setDraft(null); setError(''); }}>Cancel</button>
      <button type="submit" className="btn btn-primary" disabled={saving || !optionsReady}>{saving ? 'Saving...' : 'Save changes'}</button>
    </div>
  </form>;

  if (!isCollaborativeTicket(ticket)) return children;
  return <>
    <div className="ticket-section-heading"><h3>{isL1 ? 'L1 Master configuration' : paymentGateway ? 'Payment gateway configuration' : 'Provider configuration'}</h3>
      <div className="ticket-config-actions">{notice && <span role="status" className="ticket-save-status">{notice}</span>}{editButton || (draft
      ? <button type="button" className="btn btn-secondary" disabled={saving} onClick={() => { setDraft(null); setError(''); }}>Cancel editing</button>
      : null)}</div></div>
    {!draft && error && <p role="alert" className="ticket-inline-notice is-error">{error}</p>}
    {canEdit && draft ? form : children}
  </>;
};

export default TicketRequestEditor;
